import { QueryKeys } from "@/constants";
import type {
  DataAccessConfigResponseType,
  DataAccessListItem,
  DataAccessListResponseType,
} from "@/model/data-access";
import {
  fetchDataAccessConfig,
  fetchDataAccessList,
  updateDataAccess,
} from "@/services/data-access-service";
import type {
  DataAccessConfigPayload,
  DataAccessListPayload,
  DataAccessUpdatePayload,
} from "@/types";
import type { UseQueryResult } from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useUserInfo from "./use-user-info";
import { queryClient } from "./use-query-client";
import type { Demographics } from "@/model/demographics";

const useDataAccessConfigQuery = (): UseQueryResult<
  DataAccessConfigResponseType,
  Error
> => {
  const userInfo = useUserInfo();
  const demographics = queryClient.getQueryData([
    QueryKeys.DEMOGRAPHICS,
    userInfo.username,
  ]) as Demographics | undefined;
  const configPayload: DataAccessConfigPayload = {
    divisionId: userInfo.divisionId,
    groupId: userInfo.groupId,
    memberId: demographics?.memberId ?? "",
    planId: demographics?.planId ?? "",
    token: userInfo.accessToken,
  };

  return useQuery({
    queryKey: [QueryKeys.DATA_ACCESS_CONFIG],
    queryFn: async () => {
      const configResponse = await fetchDataAccessConfig(configPayload);
      return configResponse.results;
    },
    enabled:
      Boolean(configPayload?.divisionId) &&
      Boolean(configPayload?.groupId) &&
      Boolean(configPayload?.memberId) &&
      Boolean(configPayload?.planId) &&
      Boolean(configPayload?.token) &&
      Boolean(demographics?.hipaaProtected),
    staleTime: Infinity,
  });
};

const useDataAccessListQuery = (
  dataAccessConfig: DataAccessConfigResponseType,
): UseQueryResult<DataAccessListResponseType, Error> => {
  const userInfo = useUserInfo();
  const demographics = queryClient.getQueryData([
    QueryKeys.DEMOGRAPHICS,
    userInfo.username,
  ]) as Demographics | undefined;
  const dataAccessListPayload: DataAccessListPayload = {
    groupId: userInfo.groupId,
    memberId: demographics?.memberId ?? "",
    token: userInfo.accessToken,
  };
  return useQuery({
    queryKey: [QueryKeys.DATA_ACCESS_LIST],
    queryFn: async () => {
      const dataAccessListResponse = await fetchDataAccessList(
        dataAccessListPayload,
      );
      return dataAccessListResponse.data;
    },
    enabled: Boolean(dataAccessListPayload) && Boolean(dataAccessConfig),
  });
};

const useDataAccessUpdateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateDataAccess,
    // Optimistic update
    onMutate: (accessItemToUpdate: DataAccessUpdatePayload) => {
      // cancel any outgoing refetch
      queryClient.cancelQueries({ queryKey: [QueryKeys.DATA_ACCESS_LIST] });
      // snapshot previos data access list
      const previousDataAccessList = queryClient.getQueryData<
        DataAccessListItem[]
      >([QueryKeys.DATA_ACCESS_LIST]);
      // optimistically update data access list
      queryClient.setQueryData(
        [QueryKeys.DATA_ACCESS_LIST],
        (cachedDataAccessList: DataAccessListItem[] | undefined) => {
          const accessItemToUpdateClone: Partial<{
            grantedBy: string;
            grantedTo: string;
            status: string;
            token: string;
            memberId: string;
          }> = { ...accessItemToUpdate }; // Shallow copy
          delete accessItemToUpdateClone.memberId;
          delete accessItemToUpdateClone.token;
          // initially list is empty
          if (cachedDataAccessList?.length === 0) {
            return [
              { ...accessItemToUpdateClone, date: new Date().toISOString() },
            ];
          } else {
            // list is not empty but record is still not created
            if (
              !cachedDataAccessList?.some(
                (accessItem) =>
                  accessItem?.grantedBy ===
                    accessItemToUpdateClone?.grantedBy &&
                  accessItem?.grantedTo === accessItemToUpdateClone?.grantedTo,
              )
            ) {
              return [
                ...(cachedDataAccessList as DataAccessListItem[]),
                { ...accessItemToUpdateClone, date: new Date().toISOString() },
              ];
            }
            // list is not empty record exists
            else {
              return cachedDataAccessList?.map((accessListItem) =>
                accessListItem.grantedBy === accessItemToUpdate.grantedBy &&
                accessListItem.grantedTo === accessItemToUpdate.grantedTo
                  ? {
                      ...accessItemToUpdateClone,
                      date: new Date().toISOString(),
                    } // assume status is update
                  : accessListItem,
              );
            }
          }
        },
      );
      return { previousDataAccessList };
    },
    // On success, replace the optimistic update with server response
    onSuccess: (updatedAccessListItem: DataAccessListItem) => {
      queryClient.setQueryData(
        [QueryKeys.DATA_ACCESS_LIST],
        (cachedDataAccessList: DataAccessListItem[] | undefined) => {
          return (
            cachedDataAccessList?.map((accessListItem) =>
              accessListItem.grantedBy === updatedAccessListItem.grantedBy &&
              accessListItem.grantedTo === updatedAccessListItem.grantedTo
                ? updatedAccessListItem
                : accessListItem,
            ) ?? []
          );
        },
      );
    },
    // On error, rollback
    onError: (
      _error: unknown,
      _accessItemToUpdate: DataAccessUpdatePayload,
      context:
        | { previousDataAccessList: DataAccessListItem[] | undefined }
        | undefined,
    ) => {
      if (context?.previousDataAccessList) {
        queryClient.setQueryData(
          [QueryKeys.DATA_ACCESS_LIST],
          context?.previousDataAccessList,
        );
      }
    },
  });
};

export {
  useDataAccessConfigQuery,
  useDataAccessListQuery,
  useDataAccessUpdateMutation,
};
